import 'moment-timezone/builds/moment-timezone-with-data-2012-2022';
import 'moment-range';

// eslint-disable-next-line no-restricted-imports
import '../assets/styles/index.beta.scss';
import 'normalize.css/normalize.css';
import 'angular-pickadate/src/angular-pickadate.scss';

import './polyfills';

import angular from 'angular';
import { UI_ROUTER_REACT_HYBRID } from '@uirouter/react-hybrid';
import ngResource from 'angular-resource';
import ngAnimate from 'angular-animate';
import ngSanitize from 'angular-sanitize';
import gettext from 'angular-gettext';
import 'angular-pickadate/src/angular-pickadate';
import Raven from 'raven-js';
import ngRaven from 'raven-js/plugins/angular';
import oclazyload from 'oclazyload';
import angularElastic from 'angular-elastic';
import ngRedux from 'ng-redux';
import { isServiceWorkerSupported } from '@adjust/dashboard-core/lib/serviceWorker/serviceWorker';
import storageMigration from '@adjust/dashboard-core/lib/storageMigration/storageMigration';
import errorHandler, { enableGlobalErrorHandler } from '@adjust/dashboard-core/lib/errorHandler/errorHandler';
import { ErrorHandlerService } from '@adjust/dashboard-core/app/error-handler/error-handler.service';
import { LanguageLoader } from '@adjust/dashboard-core/app/route-resolver/language-loader/language-loader.service';
import {
  deprecationMiddleware,
  runDeprecationMiddleware,
} from '@adjust/dashboard-components/build/deprecation/angularDeprecationMiddleware';

import reactModule from '@react/reactModule';
import { rootReducer, middleware, enhancers, runSagaMiddleware, areDevToolsEnabled } from '@redux/store';
import common from './components/components';
import accountSettings from './account-settings/account-settings';
import errorPage from './error-page/error-page';
import externalRoutes from './external-routes';

import { RouteResolver } from './route-resolver/route-resolver.service';
import { MainController } from './main/main.controller';

Raven.config('https://148098c5302a459d9cbfbd21240de783@o34917.ingest.sentry.io/5679040', {
  shouldSendCallback: () => !__IS_DEV__,
  release: __APP_VERSION__,
  autoBreadcrumbs: {
    console: false,
  },
})
  .addPlugin(ngRaven, angular)
  .install();

enableGlobalErrorHandler();

export default angular
  .module('adjust', [
    ngResource,
    UI_ROUTER_REACT_HYBRID,
    ngAnimate,
    ngSanitize,
    gettext,
    oclazyload,
    'pickadate',
    angularElastic,
    ngRedux,
    ngRaven.moduleName,

    common,
    reactModule,
    errorPage,
    accountSettings,
    externalRoutes,
  ])
  .service('ErrorHandlerService', ErrorHandlerService)
  .factory('RouteResolver', RouteResolver)
  .factory('deprecationMiddleware', deprecationMiddleware)
  .service('LanguageLoader', LanguageLoader)
  .controller('MainController', MainController)
  .config(config)
  .run(run).name;

function config(
  $logProvider,
  $httpProvider,
  $compileProvider,
  $animateProvider,
  conf,
  $ngReduxProvider,
  $provide,
  $locationProvider,
) {
  'ngInject';

  $locationProvider.html5Mode(true);

  $logProvider.debugEnabled(__IS_DEV__);

  $httpProvider.interceptors.push('ErrorInterceptor');
  $httpProvider.interceptors.push('Transformer');
  $httpProvider.interceptors.push('AssetRequestInterceptor');
  $httpProvider.interceptors.push('RedirectInterceptor');

  if (isServiceWorkerSupported()) {
    $httpProvider.interceptors.push('ServiceWorkerInterceptor');
  }

  $compileProvider.debugInfoEnabled(__IS_DEV__);

  $animateProvider.classNameFilter(/animate/);

  Raven.setTagsContext({ environment: conf.environment });

  $ngReduxProvider.createStoreWith(rootReducer, middleware, enhancers);

  ErrorHandlerService($provide);
}

function run(gettextCatalog, conf, $rootScope, $timeout, $state, $ngRedux, $injector, $transitions) {
  'ngInject';

  gettextCatalog.debug = !conf.isProduction;

  // Do not log router transition errors automatically
  $state.defaultErrorHandler(() => {});
  $state.originalHrefFunction = $state.href;
  $state.href = function href(stateOrName, params, options) {
    const targetHref = $state.originalHrefFunction(stateOrName, params, options);
    return targetHref && targetHref.startsWith('/account#') ? targetHref.slice(8) : targetHref;
  };

  const _unregisterClearLoading = $rootScope.$on('global:clearLoading', _handleClearLoading);

  /**
   * @param {String} str
   * @param {Object} tokens
   * @returns {String}
   */
  Object.defineProperty(window, 't', {
    value: (str, tokens) => gettextCatalog.getString(str, tokens),
    writable: false,
  });

  /**
   * @param {String} str
   * @returns {String}
   */
  Object.defineProperty(window, 'gettext', {
    value: str => str,
    writable: false,
  });

  // Runs the migrations given the current version
  try {
    storageMigration.run(4);
  } catch (e) {
    errorHandler.trackError(new Error(e));
  }

  runSagaMiddleware();

  // Extra eval to fix dev tools in dev mode
  areDevToolsEnabled() &&
    $ngRedux.subscribe(() => {
      $rootScope.$evalAsync();
    });

  /**
   * @private
   */
  function _handleClearLoading() {
    $timeout(() => {
      try {
        const el = document.querySelector('#global-loading');
        el.parentNode.removeChild(el);
        _unregisterClearLoading();
      } catch (e) {} // eslint-disable-line
    });
  }

  runDeprecationMiddleware($transitions, $injector, {
    projectName: 'dashboard-ui-accounts',
  });
}
